import type { ProductionBoardItem, WorkflowTemplate } from '@nucleus-commerce/api-client/admin';

export interface BoardColumn {
  /** Stage key; null for cards whose stage the default template does not have. */
  key: string | null;
  name: string;
  items: ProductionBoardItem[];
}

/**
 * One column per stage of the default template, in its order, then one column for cards at a
 * stage only another template has (orders switched to it, or running an older default).
 * Without the template (no settings.read), columns follow the stages the cards are at.
 */
export function boardColumns(
  items: readonly ProductionBoardItem[],
  template: WorkflowTemplate | undefined,
  otherName: string,
): BoardColumn[] {
  if (template) {
    const known = new Set(template.stages.map((s) => s.key));
    const columns: BoardColumn[] = template.stages.map((stage) => ({
      key: stage.key,
      name: stage.name,
      items: items.filter((item) => item.stageKey === stage.key),
    }));
    const others = items.filter((item) => item.stageKey === null || !known.has(item.stageKey));
    if (others.length > 0) columns.push({ key: null, name: otherName, items: others });
    return columns;
  }
  const columns = new Map<string, BoardColumn>();
  for (const item of items) {
    const key = item.stageKey ?? '';
    const column = columns.get(key) ?? {
      key: item.stageKey,
      name: item.stageName ?? otherName,
      items: [],
    };
    column.items.push(item);
    columns.set(key, column);
  }
  return [...columns.values()];
}
